<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const emit = defineEmits(['confirm', 'reevaluateChange']);

const { t } = useI18n();

const dialogRef = ref(null);
const preview = ref(null);
const isLoading = ref(false);
const reevaluate = ref(false);

const nothingToDo = computed(
  () => preview.value !== null && preview.value.to_evaluate === 0
);

// A per-conversation cost is fractions of a cent, and a short run's total is not much more, so
// two decimals would print $0.00 for both and tell the reader nothing. Small amounts get four.
const formatUsd = (value, digits) => {
  const amount = value ?? 0;
  const decimals = digits ?? (Math.abs(amount) < 1 ? 4 : 2);
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(amount);
};

const scopeRows = computed(() => {
  if (!preview.value) return [];
  return [
    {
      label: t('ANALYSIS.QUALITY.PREVIEW.TOTAL'),
      value: preview.value.total_conversations,
    },
    {
      label: t('ANALYSIS.QUALITY.PREVIEW.ALREADY'),
      value: preview.value.already_evaluated,
    },
    {
      label: t('ANALYSIS.QUALITY.PREVIEW.TO_EVALUATE'),
      value: preview.value.to_evaluate,
      strong: true,
    },
  ];
});

const runRows = computed(() => {
  if (!preview.value) return [];
  return [
    {
      label: t('ANALYSIS.QUALITY.PREVIEW.CRITERIA'),
      value: preview.value.criteria_count,
    },
    { label: t('ANALYSIS.QUALITY.PREVIEW.MODEL'), value: preview.value.model },
    {
      label: t('ANALYSIS.QUALITY.PREVIEW.TOKENS'),
      value: t('ANALYSIS.QUALITY.PREVIEW.TOKENS_VALUE', {
        input: preview.value.input_tokens_per_conversation,
        output: preview.value.output_tokens_per_conversation,
      }),
    },
  ];
});

const onToggleReevaluate = value => {
  reevaluate.value = value;
  emit('reevaluateChange', value);
};

const open = () => {
  reevaluate.value = false;
  dialogRef.value.open();
};

const close = () => dialogRef.value.close();

const setPreview = value => {
  preview.value = value;
};

const setLoading = value => {
  isLoading.value = value;
};

defineExpose({ open, close, setPreview, setLoading });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="t('ANALYSIS.QUALITY.PREVIEW.TITLE')"
    :confirm-button-label="t('ANALYSIS.QUALITY.PREVIEW.CONFIRM')"
    :cancel-button-label="t('ANALYSIS.QUALITY.PREVIEW.CANCEL')"
    :disable-confirm-button="isLoading || nothingToDo"
    @confirm="emit('confirm', { reevaluate })"
  >
    <div v-if="isLoading" class="flex justify-center py-8">
      <Spinner />
    </div>
    <div v-else-if="preview" class="flex flex-col gap-4">
      <p class="text-sm text-n-slate-11">
        {{ t('ANALYSIS.QUALITY.PREVIEW.INTRO') }}
      </p>

      <div class="flex flex-col gap-2">
        <div
          v-for="row in scopeRows"
          :key="row.label"
          class="flex items-center justify-between gap-4 text-sm"
        >
          <span class="text-n-slate-11">{{ row.label }}</span>
          <span
            class="tabular-nums text-n-slate-12"
            :class="row.strong && 'font-semibold'"
          >
            {{ row.value }}
          </span>
        </div>
      </div>

      <div class="flex flex-col gap-2 pt-3 border-t border-n-weak">
        <div
          v-for="row in runRows"
          :key="row.label"
          class="flex items-center justify-between gap-4 text-sm"
        >
          <span class="text-n-slate-11">{{ row.label }}</span>
          <span class="tabular-nums text-n-slate-12">{{ row.value }}</span>
        </div>
      </div>

      <div class="flex flex-col gap-1 p-3 rounded-lg bg-n-alpha-1">
        <div class="flex items-baseline justify-between gap-4">
          <span class="text-sm text-n-slate-11">
            {{ t('ANALYSIS.QUALITY.PREVIEW.COST') }}
          </span>
          <span class="text-lg font-semibold text-n-slate-12 tabular-nums">
            {{ formatUsd(preview.usd_total) }}
          </span>
        </div>
        <span class="text-xs text-n-slate-11 text-right tabular-nums">
          {{
            t('ANALYSIS.QUALITY.PREVIEW.COST_PER', {
              amount: formatUsd(preview.usd_per_conversation, 4),
            })
          }}
        </span>
        <p class="text-xs text-n-slate-11 mt-1">
          {{ t('ANALYSIS.QUALITY.PREVIEW.COST_NOTE') }}
        </p>
      </div>

      <div class="flex items-center gap-3">
        <Switch
          :model-value="reevaluate"
          @update:model-value="onToggleReevaluate"
        />
        <span class="text-sm text-n-slate-12">
          {{ t('ANALYSIS.QUALITY.PREVIEW.REEVALUATE') }}
        </span>
      </div>

      <p v-if="nothingToDo" class="text-sm text-n-amber-11">
        {{ t('ANALYSIS.QUALITY.PREVIEW.NOTHING') }}
      </p>
    </div>
  </Dialog>
</template>
